import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import { DUO_COLUMN_WIDTH_MD } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import * as S from './DuoImageModule.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { Image } from '@rocco/components/image';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail-mobile/detail-modules/DetailModules.style';
import LazyImageModule from '../detail-modules/LazyImageModule';

/** Number of modules to render eagerly (above-fold) */
const EAGER_COUNT = 2;

interface DuoImageModuleProps {
    columns: ImageColumnProps[];
    /** Index of this module for eager loading determination */
    moduleIndex?: number;
}

/**
 * Mobile-optimised duo image module displaying two images side by side.
 * Uses mobile-specific image sizes (~207px per image) for better performance.
 */
const DuoImageModule = ({ columns, moduleIndex = 0 }: DuoImageModuleProps) => {
    const { mobile } = useAppDisplayModeContext();
    const isEager = moduleIndex < EAGER_COUNT;

    const { css } = useStyles({
        columns: columns.filter((c) => mobile && c.imageUrl).length,
        mobile,
    });

    return (
        <div
            className={css(S.DuoImageModuleContainer, ImageModuleExtraSpacing)}
        >
            {columns.map((column) => {
                const isMedium = column.dimension.width >= DUO_COLUMN_WIDTH_MD;
                if (mobile && !column.imageUrl) {
                    return null;
                }

                if (!column?.imageUrl) {
                    return null;
                }

                return (
                    <LazyImageModule
                        key={column.id}
                        className={css(S.DuoImageContainerImage(isMedium))}
                        dimension={column.dimension}
                        moduleIndex={moduleIndex}
                        eagerCount={EAGER_COUNT}
                    >
                        <DetailSingleImageShell
                            caption={column.caption}
                            dimension={column.dimension}
                            libraryImage={column?.libraryImage}
                            tags={column.tags}
                            uniqueApprovedTags={column.uniqueApprovedTags}
                            imageURL={column.imageUrl}
                        >
                            <Image
                                src={column.imageUrl}
                                width={column.dimension.width ?? undefined}
                                height={column.dimension.height ?? undefined}
                                fill
                                fit="cover"
                                loading={isEager ? 'eager' : 'lazy'}
                                fetchPriority={isEager ? 'high' : 'low'}
                                sizes={getImagesSizes({
                                    desktop: column.dimension.width,
                                    mobile: column.dimension.mobileWidth,
                                })}
                                alt={
                                    column.caption ||
                                    column?.libraryImage?.Title ||
                                    ''
                                }
                            />
                        </DetailSingleImageShell>
                    </LazyImageModule>
                );
            })}
        </div>
    );
};

export default DuoImageModule;
